import type { ReactNode } from 'react';
import type { NoteRecord } from '@/types/note';

interface NoteSheetProps {
  note: NoteRecord;
  onChange: (patch: Partial<NoteRecord>) => void;
  onDelete: () => void;
  /** true면 입력 컨트롤 대신 텍스트로만 그립니다 (PDF 내보내기용 정적 시트) */
  paperMode?: boolean;
}

const DIFFICULTY_OPTIONS = [
  { key: 'easy3', label: '쉬운 3점', color: 'text-green-600' },
  { key: 'hard3', label: '어려운 3점', color: 'text-green-600' },
  { key: 'easy4', label: '쉬운 4점', color: 'text-orange-500' },
  { key: 'hard4', label: '어려운 4점', color: 'text-red-500' },
] as const;

const EMPTY_SPACE = '\u00A0';

function SectionLabel({ children }: { children: ReactNode }) {
  return <span className="text-[10px] font-bold text-foreground-900">{children}</span>;
}

function ThinInput({
  value,
  onChange,
  placeholder,
  className = '',
  paperMode = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  paperMode?: boolean;
}) {
  if (paperMode) {
    return (
      <span className={`block w-full px-1 py-0.5 text-[10px] text-foreground-900 ${className}`}>
        {value || EMPTY_SPACE}
      </span>
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-transparent px-1 py-0.5 text-[10px] text-foreground-900 outline-none ${className}`}
    />
  );
}

export default function NoteSheet({ note, onChange, onDelete, paperMode = false }: NoteSheetProps) {
  return (
    <div className="print-sheet relative mx-auto w-full max-w-[800px] overflow-hidden border-2 border-foreground-900 bg-white">
      {/* 화면 전용 삭제 버튼 */}
      {!paperMode && (
        <button
          type="button"
          onClick={onDelete}
          className="print-hide absolute right-2 top-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-foreground-200 bg-white text-foreground-400 hover:text-red-500"
          aria-label="삭제"
        >
          <i className="ri-delete-bin-6-line text-sm"></i>
        </button>
      )}

      {/* ── 상단 정보 테이블: 문제출처 | 정답 | 난이도 4칸 ── */}
      <div className="grid grid-cols-[70px_1fr_40px_50px_55px_55px_55px_55px] border-b-2 border-foreground-900">
        {/* Row 1 */}
        <div className="flex items-center justify-center border-r border-foreground-900 py-2">
          <SectionLabel>문제출처</SectionLabel>
        </div>
        <div className="flex items-center border-r border-foreground-900 px-2 py-2">
          {/* 문제출처: 인쇄 후 직접 작성할 수 있도록 빈칸 유지 */}
        </div>
        <div className="flex items-center justify-center border-r border-foreground-900 py-2">
          <SectionLabel>정답</SectionLabel>
        </div>
        <div className="flex items-center border-r border-foreground-900 px-2 py-2">
          <ThinInput
            value={note.chapter}
            onChange={(v) => onChange({ chapter: v })}
            className="text-center"
            paperMode={paperMode}
          />
        </div>
        {/* 난이도 4칸 */}
        {DIFFICULTY_OPTIONS.map((opt) => {
          const active = note.problemType === opt.key;
          const content = (
            <>
              <span className={opt.color}>{opt.label.split(' ')[0]}</span>
              <span className={opt.color}>{opt.label.split(' ')[1]}</span>
              <div
                className={`mt-0.5 h-3 w-3 rounded-sm border border-foreground-400 ${
                  active ? 'bg-foreground-800' : 'bg-white'
                }`}
              />
            </>
          );
          if (paperMode) {
            return (
              <div
                key={opt.key}
                className={`flex flex-col items-center justify-center gap-0 border-r border-foreground-900 py-1 text-[9px] font-bold last:border-r-0 ${
                  active ? 'bg-foreground-100' : ''
                }`}
              >
                {content}
              </div>
            );
          }
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange({ problemType: opt.key })}
              className={`flex cursor-pointer flex-col items-center justify-center gap-0 border-r border-foreground-900 py-1 text-[9px] font-bold transition last:border-r-0 ${
                active ? 'bg-foreground-100' : ''
              }`}
            >
              {content}
            </button>
          );
        })}
      </div>

      {/* ── 중앙: 문제 + 풀이 ── */}
      <div
        className="sheet-main grid border-b-2 border-foreground-900"
        style={{ gridTemplateColumns: 'calc(50% - 1cm) 1fr' }}
      >
        {/* 왼쪽: 문제 */}
        <div className="flex flex-col border-r border-foreground-900">
          <div className="flex items-center justify-center border-b border-foreground-900 py-1.5">
            <SectionLabel>문제</SectionLabel>
          </div>
          <div className="problem-img-wrap flex flex-1 items-start justify-center overflow-hidden p-3">
            {note.problemImage ? (
              <img
                src={note.problemImage}
                alt={note.problemText || '캡처한 문제'}
                title={note.problemText || '캡처한 문제'}
                className="h-auto max-h-full w-full object-contain object-top"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-center text-xs text-foreground-400">
                'PDF에서 캡처' 탭에서 문제 영역을 드래그하면
                <br />이 자리에 문제 이미지가 들어갑니다
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 풀이 */}
        <div className="flex flex-col">
          <div className="flex items-center justify-center border-b border-foreground-900 py-1.5">
            <SectionLabel>풀이</SectionLabel>
          </div>
          <div className="flex flex-1 flex-col">
            {paperMode ? (
              <div className="h-full w-full whitespace-pre-wrap px-3 py-2 text-sm leading-relaxed text-foreground-900">
                {note.retrySolution || EMPTY_SPACE}
              </div>
            ) : (
              <textarea
                className="h-full w-full resize-none bg-white px-3 py-2 text-sm leading-relaxed text-foreground-900 outline-none"
                value={note.retrySolution}
                onChange={(e) => onChange({ retrySolution: e.target.value })}
                placeholder=""
              />
            )}
          </div>
        </div>
      </div>

      {/* ── 문제 조건 정리 ── */}
      <div className="grid grid-cols-[1fr_60px] border-b-2 border-foreground-900">
        <div className="flex items-center justify-center border-r border-foreground-900 py-1.5">
          <SectionLabel>문제 조건 정리</SectionLabel>
        </div>
        <div className="flex items-center justify-center py-1.5">
          <SectionLabel>체크</SectionLabel>
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="contents">
            <div className="flex items-center border-r border-t border-foreground-900 px-2 py-1.5 text-xs text-foreground-900">
              <span className="mr-1 font-bold">{i + 1}.</span>
              {paperMode ? (
                <span className="w-full whitespace-pre-wrap text-xs">
                  {(note.wrongReason ?? '').split('\n')[i] || EMPTY_SPACE}
                </span>
              ) : (
                <input
                  type="text"
                  value={note.wrongReason?.split('\n')[i] ?? ''}
                  onChange={(e) => {
                    const lines = (note.wrongReason ?? '').split('\n');
                    lines[i] = e.target.value;
                    onChange({ wrongReason: lines.join('\n') });
                  }}
                  className="w-full bg-transparent text-xs outline-none"
                />
              )}
            </div>
            <div className="flex items-center justify-center border-t border-foreground-900 py-1.5">
              <div className="h-3.5 w-3.5 border border-foreground-900" />
            </div>
          </div>
        ))}
      </div>

      {/* ── 핵심 아이디어 / 개념 정리 ── */}
      <div className="border-b-2 border-foreground-900 py-1.5 text-center">
        <span className="text-xs font-bold text-foreground-900">
          ★ 문제 핵심 아이디어 or 모르거나 헷갈렸던 개념(공식) or 실수 정리노트
        </span>
      </div>
      <div className="sheet-keypoint min-h-[140px] bg-white p-3">
        {paperMode ? (
          <div className="h-full w-full whitespace-pre-wrap text-sm leading-relaxed text-foreground-900">
            {note.keyPoint || EMPTY_SPACE}
          </div>
        ) : (
          <textarea
            className="h-full w-full resize-none bg-transparent text-sm leading-relaxed text-foreground-900 outline-none"
            value={note.keyPoint}
            onChange={(e) => onChange({ keyPoint: e.target.value })}
            placeholder=""
          />
        )}
      </div>

      {/* 인쇄 시 푸터 */}
      <div className="print-footer items-center justify-between border-t border-foreground-200 px-3 py-1 text-[10px] text-foreground-400">
        <span>오답노트 메이커</span>
        <span className="font-semibold">복습은 반복이 핵심입니다</span>
      </div>
    </div>
  );
}